/* eslint-disable no-param-reassign */
import { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { Icon } from "@mui/material";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "components/Tables/DataTable";

import MDButton from "components/MDButton";
import qrCodeHelpers from "layouts/qrCode/data/qrCodes";
import Loading from "components/Loading";

import { actions } from "../../store/reducers/qrCodes";
import { actions as numbersActions } from "../../store/reducers/numbers";
import Modal from "./modal";

function QrCode() {
  const dispatch = useDispatch();

  const { getRows, columns } = qrCodeHelpers();
  const [qrCodeToEdit, setQrCodeToEdit] = useState(null);

  const { qrCodes, totalItems, page, perPage, qs, loading } = useSelector((state) => state.qrCodes);

  const locations = useSelector((state) => state.numbers.locations);
  const timezones = useSelector((state) => state.numbers.timezones);

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
    setQrCodeToEdit(null);
  }, [setOpen, setQrCodeToEdit]);

  const submit = useCallback(
    (data, call) => {
      if (qrCodeToEdit) {
        dispatch(
          actions.updateQrCode(
            qrCodeToEdit.id,
            data,
            () => {
              toast.success("QR code updated successfully");
              handleClose();
            },
            (msg, error) => {
              toast.error(msg || "QR code could not be updated");
              call(error);
            }
          )
        );
      } else {
        dispatch(
          actions.addQrCode(
            data,
            () => {
              toast.success("QR code created successfully");
              handleClose();
            },
            (msg, error) => {
              toast.error(msg || "QR code could not be created");
              call(error);
            }
          )
        );
      }
    },
    [qrCodeToEdit, dispatch, handleClose]
  );

  useEffect(() => {
    dispatch(numbersActions.fetchLocationRequest((err) => toast.error(err)));
    dispatch(numbersActions.fetchTimezoneRequest((err) => toast.error(err)));
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.fetchQrCodeRequest(page, perPage, (err) => toast.error(err)));
  }, [page, perPage, qs]);

  const handleExport = () =>
    dispatch(actions.exportQrCodeRequest(page, perPage, (err) => toast.error(err)));
  const rows = useMemo(
    () =>
      getRows(
        qrCodes,
        locations.reduce((all, l) => {
          all[l.id] = l;
          return all;
        }, {}),
        timezones.reduce((all, l) => {
          all[l.id] = l;
          return all;
        }, {}),
        setQrCodeToEdit
      ),
    [qrCodes, locations, timezones, setQrCodeToEdit]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loading />}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  QR codes
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <MDBox pl={3}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="medium"
                    disabled={totalItems === 0}
                    aria-label="close"
                    onClick={handleExport}
                  >
                    <Icon fontSize="medium">download</Icon> Export CSV
                  </MDButton>
                  <MDButton
                    style={{ marginLeft: "20px" }}
                    variant="gradient"
                    color="primary"
                    size="medium"
                    aria-label="close"
                    onClick={handleOpen}
                  >
                    <Icon fontSize="medium">add</Icon> Add QR code
                  </MDButton>
                </MDBox>

                <DataTable
                  entriesPerPage={{ defaultValue: perPage, entries: [5, 10, 15, 20, 50] }}
                  table={{ columns, rows }}
                  isSorted={false}
                  showTotalEntries
                  noEndBorder
                  canSearch
                  totalCount={totalItems}
                  page={page}
                  perPage={perPage}
                  qs={qs}
                  setQs={(s) => dispatch(actions.setQrCodeQs(s))}
                  gotoPage={(p) => dispatch(actions.setQrCodePage(p))}
                  setPageSize={(p) => dispatch(actions.setQrCodePerPage(p))}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Modal
        open={!!(open || qrCodeToEdit)}
        handleClose={handleClose}
        onSumbit={submit}
        data={qrCodeToEdit}
        isEdit={qrCodeToEdit !== null}
        locations={locations}
        timezones={timezones}
      />
    </DashboardLayout>
  );
}

export default QrCode;
